/**
 * The board's save queue: one writer, one lock, one chain.
 *
 * Every change schedules a write, debounced so typing is one save and not one
 * per keystroke. Writes are chained through a single promise, so two of them can
 * never leave on the same version and turn into a false "changed somewhere
 * else". A tab going away flushes what it holds, even mid-write: the second
 * request is a keepalive one, and the server's own lock decides which lands.
 *
 * While a card operation is on its way the queue holds its writes (`hold`):
 * they would leave on the version the operation is about to replace. The
 * operation's answer resets the queue, the changes made meanwhile are laid over
 * it, and `release` lets the result go (specs/formal/board-edits).
 *
 * The queue takes its api and its clock as arguments, so the whole of it is
 * exercised in `test/board-state.test.ts` with no network and no browser.
 */
import type { BoardSaveResponse, BoardState, BoardStateResponse } from "../../shared/types";
import { ApiError } from "../lib/api";

/** What the host is told when another tab (or window) got there first. */
export const BOARD_RELOADED = "This board changed somewhere else. Reloaded.";
/** What the host is told when the board has more in it than we can store. */
export const BOARD_TOO_BIG = "This board has more in it than we can save. Delete a card or two.";
/** The quiet retry line: the next change writes again anyway. */
const BOARD_SAVE_LATER = "We could not save that just now. We will try again.";
/** A change the server would not take. Its own words follow. */
export const BOARD_REFUSED = "That change was not kept:";

/** How long typing settles before the board is written. */
export const SAVE_DEBOUNCE_MS = 600;
/**
 * A leaving tab may only send about 64 KiB with `keepalive`. A board bigger than
 * this is written on every change instead of on a pause, so the flush on the way
 * out has nothing left to carry.
 */
export const KEEPALIVE_SAFE_BYTES = 56 * 1024;

interface BoardSaveApi {
  save(
    id: string,
    body: { version: number; state: BoardState },
    init?: RequestInit,
  ): Promise<BoardSaveResponse>;
  get(id: string): Promise<BoardStateResponse>;
}

interface BoardSaveHooks {
  /** The board is saved: this version goes on the next write. */
  onSaved(version: number): void;
  /** The server's board, after somebody else saved first or a change was refused. */
  onReloaded(server: BoardStateResponse): void;
  /** Something to tell the host, in their words. */
  onMessage(message: string): void;
}

interface BoardSaveOptions {
  webinarId: string;
  api: BoardSaveApi;
  hooks: BoardSaveHooks;
  /** The version the next write is based on. */
  version: number;
  debounceMs?: number;
}

/** How long a board this size should wait before it is written. */
export function debounceFor(json: string, base = SAVE_DEBOUNCE_MS): number {
  return json.length > KEEPALIVE_SAFE_BYTES ? 0 : base;
}

/**
 * One board's writer. `queue` schedules, `flush` writes now (the leaving path),
 * `stop` drops anything pending because the view is going away.
 */
export class BoardSaveQueue {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private pending: BoardState | null = null;
  /** The tail of the write chain: every send waits for the one before it. */
  private chain: Promise<void> = Promise.resolve();
  private inFlight = false;
  private lastJson = "";
  private stopped = false;
  /** A card operation is on its way: changes wait, unsent. */
  private held = false;
  private readonly base: number;

  /** The version the next write carries. Replaced by every answer the server gives. */
  version = 0;

  constructor(private readonly opts: BoardSaveOptions) {
    this.base = opts.debounceMs ?? SAVE_DEBOUNCE_MS;
    this.version = opts.version;
  }

  /** Start (or restart) the queue on a board the server just handed us. */
  reset(state: BoardState, version: number): void {
    this.clear();
    this.version = version;
    this.pending = null;
    this.lastJson = JSON.stringify(state);
    this.stopped = false;
  }

  /** Schedule a write. Two changes inside the window are one write. */
  queue(state: BoardState): void {
    if (this.stopped) return;
    this.pending = state;
    this.clear();
    if (this.held) return;
    const wait = debounceFor(JSON.stringify(state), this.base);
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.write();
    }, wait);
  }

  /**
   * Write now.
   *
   * A keepalive flush never goes through the chain: the chain starts in a
   * microtask, and the teardown that follows a leaving flush (`stop`, the route
   * change) beats a microtask every time. It sends straight away instead, from
   * the same tick the caller is in, so the request is in the browser's hands
   * before anything can cancel it.
   *
   * `race` separates the two ways a tab goes quiet. On `pagehide` there is no
   * later, so a write already in flight is raced (the server's version lock
   * decides, and the loser says nothing). On a tab switch there IS a later, so
   * the in-flight write is waited for and the newer board goes out on the
   * version it produced, which is what keeps the newer edit on screen.
   */
  async flush(keepalive = false, race = false): Promise<void> {
    this.clear();
    if (!keepalive) {
      await this.write(false);
      return;
    }
    if (this.inFlight && !race) await this.chain.catch(() => undefined);
    const state = this.pending;
    if (!state) return;
    const json = JSON.stringify(state);
    if (json === this.lastJson) {
      this.pending = null;
      return;
    }
    await this.send(state, json, true);
  }

  /** Is there a change waiting to be written? */
  get dirty(): boolean {
    return this.pending !== null;
  }

  /** A card operation leaves on this version: nothing is written until it lands. A leaving flush still goes. */
  hold(): void {
    this.held = true;
    this.clear();
  }

  /** The operation landed (or did not): what waits is written as any change is. */
  release(): void {
    this.held = false;
    if (this.pending) this.queue(this.pending);
  }

  /**
   * The view is leaving: nothing NEW goes out. A send already started is left
   * alone, because it is the leaving flush that just carried the last edit.
   */
  stop(): void {
    this.clear();
    this.stopped = true;
    this.pending = null;
  }

  private clear(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
  }

  /** Put one write at the end of the chain, so two never leave on one version. */
  private write(keepalive = false): Promise<void> {
    const run = this.chain.then(async () => {
      const state = this.pending;
      if (!state || this.stopped || (this.held && !keepalive)) return;
      const json = JSON.stringify(state);
      if (json === this.lastJson) {
        this.pending = null;
        return;
      }
      await this.send(state, json, keepalive);
    });
    // A failed write must not break the chain for the ones behind it.
    this.chain = run.catch(() => undefined);
    return run;
  }

  private async send(state: BoardState, json: string, keepalive: boolean): Promise<void> {
    const { webinarId, api, hooks } = this.opts;
    this.inFlight = true;
    try {
      const res = await api.save(
        webinarId,
        { version: this.version, state },
        keepalive ? { keepalive: true } : {},
      );
      // Only clear what we sent: a change made while this was in flight stays
      // pending, so the next write carries it.
      if (this.pending && JSON.stringify(this.pending) === json) this.pending = null;
      this.lastJson = json;
      this.version = res.version;
      hooks.onSaved(res.version);
    } catch (error) {
      const status = error instanceof ApiError ? error.status : 0;
      // A keepalive write that lost a race with our own in-flight one has
      // nothing to say: reloading here would put the older board it raced
      // against back over the newer one the host is looking at.
      if (keepalive) return;
      if (status === 409 || status === 404) {
        await this.reload();
        return;
      }
      if (status === 413) {
        // Same as a refusal: a body this size will not succeed later either, so
        // it goes rather than being re-sent behind every change after it.
        await this.reload(BOARD_TOO_BIG);
        return;
      }
      if (status === 422) {
        // The board we hold is one the server will never take, so keeping it
        // would refuse every later change too. It goes, the server's board comes
        // back, and the host is told what was dropped.
        const message = error instanceof ApiError ? error.message : "";
        await this.reload(`${BOARD_REFUSED} ${message}`.trim());
        return;
      }
      hooks.onMessage(BOARD_SAVE_LATER);
    } finally {
      this.inFlight = false;
    }
  }

  /** Take the server's board: replaying our own write over it is the one thing we never do. */
  private async reload(message = BOARD_RELOADED): Promise<void> {
    const { webinarId, api, hooks } = this.opts;
    try {
      const server = await api.get(webinarId);
      this.pending = null;
      this.lastJson = JSON.stringify(server.state);
      this.version = server.version;
      hooks.onReloaded(server);
      hooks.onMessage(message);
    } catch {
      hooks.onMessage(BOARD_SAVE_LATER);
    }
  }
}
